import { useState } from "react";
import { Smartphone, Apple, Download, Share, Plus, QrCode, CheckCircle2, Wifi, Bell, MapPin, Camera } from "lucide-react";

const APP_URL = window.location.origin;

export default function MobileApp() {
  const [tab, setTab] = useState("pwa"); // pwa | native

  // QR code via API esterna (Google Chart o qrserver) — apre la PWA su un altro dispositivo
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&bgcolor=0a0a0a&color=ff0033&qzone=2&data=${encodeURIComponent(APP_URL)}`;

  return (
    <div className="space-y-6" data-testid="mobile-app-page">
      <div>
        <h1 className="font-display text-3xl font-black uppercase tracking-tight flex items-center gap-3">
          <Smartphone className="text-[#ff0033]" size={28} />
          App Mobile
        </h1>
        <p className="text-sm text-white/45 mt-1">
          Scarica FabriRent sul tuo telefono — installazione istantanea PWA oppure app nativa pubblicata sugli store.
        </p>
      </div>

      <div className="flex gap-2 border-b border-white/10">
        <button onClick={() => setTab("pwa")} data-testid="tab-pwa"
          className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wider transition ${tab === 'pwa' ? 'text-[#ff0033] border-b-2 border-[#ff0033]' : 'text-white/40 hover:text-white/70'}`}>
          📱 PWA (Subito)
        </button>
        <button onClick={() => setTab("native")} data-testid="tab-native"
          className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wider transition ${tab === 'native' ? 'text-[#ff0033] border-b-2 border-[#ff0033]' : 'text-white/40 hover:text-white/70'}`}>
          🚀 App Nativa (Store)
        </button>
      </div>

      {tab === "pwa" && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <div className="space-y-4">
            <div className="glass rounded-2xl p-5">
              <div className="font-display text-xl font-bold mb-3">⚡ Installazione PWA — 30 secondi, zero costi</div>
              <p className="text-sm text-white/60 leading-relaxed mb-4">
                FabriRent è già una <b>Progressive Web App</b>. Si installa direttamente dal browser, funziona offline, ha icona sulla home, splash screen, accesso a fotocamera, notifiche, GPS. Nessun store, nessuna approvazione.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                {[
                  { icon: Wifi, label: "Offline ready" },
                  { icon: Bell, label: "Notifiche" },
                  { icon: Camera, label: "Fotocamera" },
                  { icon: MapPin, label: "GPS" },
                ].map((f, i) => (
                  <div key={i} className="text-center p-3 rounded-xl bg-white/[0.02] border border-white/5">
                    <f.icon size={20} className="text-[#ff0033] mx-auto" />
                    <div className="text-[10px] uppercase tracking-widest text-white/50 mt-2">{f.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* iOS */}
              <div className="glass rounded-2xl p-5 border-l-4 border-l-white/30">
                <div className="flex items-center gap-2 mb-3">
                  <Apple size={20} /> <span className="font-display font-bold">iPhone & iPad</span>
                </div>
                <ol className="space-y-2 text-sm text-white/70">
                  <li className="flex gap-2"><span className="text-[#ff0033] font-bold">1.</span> Apri questa pagina in <b>Safari</b></li>
                  <li className="flex gap-2"><span className="text-[#ff0033] font-bold">2.</span> Tocca <Share size={14} className="inline mx-1" /> <b>Condividi</b></li>
                  <li className="flex gap-2"><span className="text-[#ff0033] font-bold">3.</span> Scegli <Plus size={14} className="inline mx-1" /> <b>Aggiungi a Home</b></li>
                  <li className="flex gap-2"><span className="text-[#ff0033] font-bold">4.</span> Conferma con <b>Aggiungi</b></li>
                </ol>
              </div>

              {/* Android */}
              <div className="glass rounded-2xl p-5 border-l-4 border-l-emerald-500/40">
                <div className="flex items-center gap-2 mb-3">
                  <Smartphone size={20} className="text-emerald-400" /> <span className="font-display font-bold">Android</span>
                </div>
                <ol className="space-y-2 text-sm text-white/70">
                  <li className="flex gap-2"><span className="text-emerald-400 font-bold">1.</span> Apri questa pagina in <b>Chrome</b></li>
                  <li className="flex gap-2"><span className="text-emerald-400 font-bold">2.</span> Tocca il banner <b>"Installa app"</b> in basso</li>
                  <li className="flex gap-2"><span className="text-emerald-400 font-bold">3.</span> Oppure menu ⋮ → <b>Installa app</b></li>
                  <li className="flex gap-2"><span className="text-emerald-400 font-bold">4.</span> Conferma — l'icona FabriRent appare sulla home</li>
                </ol>
              </div>
            </div>
          </div>

          {/* QR code */}
          <div className="glass rounded-2xl p-5 text-center self-start">
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Scansiona col cellulare</div>
            <div className="bg-[#0a0a0a] p-3 rounded-xl inline-block border border-[#ff0033]/30">
              <img src={qrSrc} alt="QR code FabriRent" className="w-56 h-56" />
            </div>
            <div className="text-sm font-mono text-[#ff5577] mt-3 break-all">{APP_URL}</div>
            <div className="text-[10px] text-white/40 mt-2">Apre la PWA su qualsiasi smartphone</div>
          </div>
        </div>
      )}

      {tab === "native" && (
        <div className="space-y-4">
          <div className="glass rounded-2xl p-5">
            <div className="font-display text-xl font-bold mb-3">🚀 App native pubblicabili su App Store & Google Play</div>
            <p className="text-sm text-white/60 leading-relaxed">
              Il progetto Capacitor è pronto in <code className="px-2 py-0.5 rounded bg-white/[0.05] text-[#ff5577]">/app/mobile</code>. Genera APK Android e IPA iOS con un comando — pubblica sugli store per massima credibilità.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="glass rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Smartphone size={20} className="text-emerald-400" /> <span className="font-display font-bold">Android</span>
                <span className="ml-auto text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-300 px-2 py-0.5 rounded-full">$25 una tantum</span>
              </div>
              <div className="text-xs text-white/50 mb-3">Build APK / AAB in 10 minuti</div>
              <div className="bg-black/40 rounded-lg p-3 text-[11px] font-mono text-white/70 leading-relaxed">
                <div>cd /app/mobile</div>
                <div>yarn init:android</div>
                <div>yarn build:android</div>
              </div>
              <a href="https://play.google.com/console" target="_blank" rel="noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-xs text-emerald-300 hover:text-emerald-200">
                <Download size={12} /> Google Play Console →
              </a>
            </div>

            <div className="glass rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Apple size={20} /> <span className="font-display font-bold">iOS</span>
                <span className="ml-auto text-[10px] uppercase tracking-wider bg-white/15 text-white px-2 py-0.5 rounded-full">$99/anno + Mac</span>
              </div>
              <div className="text-xs text-white/50 mb-3">Build IPA tramite Xcode (richiede Mac)</div>
              <div className="bg-black/40 rounded-lg p-3 text-[11px] font-mono text-white/70 leading-relaxed">
                <div>cd /app/mobile</div>
                <div>yarn init:ios</div>
                <div>yarn build:ios</div>
              </div>
              <a href="https://developer.apple.com" target="_blank" rel="noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-xs text-white/70 hover:text-white">
                <Download size={12} /> Apple Developer →
              </a>
            </div>
          </div>

          <div className="glass rounded-2xl p-5">
            <div className="font-display font-bold mb-2">📖 Guida completa</div>
            <p className="text-sm text-white/60 leading-relaxed">
              Vedi <code className="px-2 py-0.5 rounded bg-white/[0.05] text-[#ff5577]">/app/mobile/README.md</code> per tutti gli step (signing, certificati, pubblicazione store).
              Alternative cloud senza Mac: <b>Codemagic</b> ($28/mese) o <b>Ionic Appflow</b> ($49/mese).
            </p>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              {["@capacitor/camera", "@capacitor/geolocation", "@capacitor/push-notifications", "@capacitor/share"].map((p) => (
                <div key={p} className="px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5 text-[10px] font-mono text-white/60">
                  <CheckCircle2 size={10} className="inline text-emerald-400 mr-1" /> {p}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
